import { useMemo, useState } from 'react'
import { Card, Chip, EmptyState, LoadingRows, inputClass } from '../family-finance/shared'
import type { Position, RankedPlayer } from '../../types/fantasy-football'
import { POSITIONS } from '../../types/fantasy-football'
import { acquirePlayer } from '../../lib/fantasy-api'
import type { FantasyState } from './useFantasy'
import { fmtPts, fmtSigned, POSITION_TONES } from './useFantasy'

type Scope = 'available' | 'mine' | 'all'

export default function PlayersTab({ state }: { state: FantasyState }) {
  const { loading, ranked, recommendations, refresh } = state
  const [scope, setScope] = useState<Scope>('available')
  const [position, setPosition] = useState<Position | 'ALL'>('ALL')
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return ranked.filter((entry) => {
      if (scope === 'available' && entry.player.rosterStatus !== 'available') return false
      if (scope === 'mine' && entry.player.rosterStatus !== 'mine') return false
      if (position !== 'ALL' && entry.player.position !== position) return false
      if (q && !entry.player.name.toLowerCase().includes(q) && !entry.player.nflTeam.toLowerCase().includes(q))
        return false
      return true
    })
  }, [ranked, scope, position, search])

  async function executeAcquisition(playerId: string, dropPlayerId: string | null) {
    setBusy(true)
    try {
      await acquirePlayer(playerId, dropPlayerId)
      setConfirming(null)
      refresh()
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <LoadingRows rows={8} />

  return (
    <div className="space-y-6">
      {/* Acquisition recommendations */}
      <Card
        title="Recommended acquisitions"
        subtitle="Ranked by expected season wins added (F11) — covariance and bye effects included. Nothing executes without your confirmation."
      >
        {recommendations.length === 0 ? (
          <EmptyState icon="✅" message="No available player projects to add wins over your current roster." />
        ) : (
          <div className="space-y-2">
            {recommendations.map((rec) => (
              <div
                key={rec.playerId}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-100/60 dark:bg-slate-900/60 rounded-lg px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Chip tone={POSITION_TONES[rec.position]}>{rec.position}</Chip>
                    <p className="text-slate-900 dark:text-white text-sm font-medium">
                      Add {rec.playerName}
                      {rec.dropPlayerName && <span className="text-slate-500 dark:text-slate-400"> · drop {rec.dropPlayerName}</span>}
                    </p>
                  </div>
                  <p className="text-slate-500 text-xs mt-1">{rec.reason}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-emerald-600 dark:text-emerald-400 text-sm font-semibold">
                    {fmtSigned(rec.deltaExpectedWins, 2)} wins
                  </span>
                  {confirming === rec.playerId ? (
                    <span className="flex items-center gap-2">
                      <button
                        onClick={() => executeAcquisition(rec.playerId, rec.dropPlayerId)}
                        disabled={busy}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm rounded-lg"
                      >
                        {busy ? 'Executing…' : 'Confirm'}
                      </button>
                      <button
                        onClick={() => setConfirming(null)}
                        disabled={busy}
                        className="px-3 py-1.5 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 text-sm rounded-lg"
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={() => setConfirming(rec.playerId)}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg"
                    >
                      Acquire
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-1">
          {(['available', 'mine', 'all'] as Scope[]).map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                scope === s ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
              }`}
            >
              {s === 'available' ? 'Available' : s === 'mine' ? 'My Roster' : 'All'}
            </button>
          ))}
        </div>
        <div className="flex gap-1 overflow-x-auto">
          {(['ALL', ...POSITIONS] as (Position | 'ALL')[]).map((pos) => (
            <button
              key={pos}
              onClick={() => setPosition(pos)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                position === pos ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
              }`}
            >
              {pos}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search player or NFL team…"
          className={`${inputClass} sm:max-w-xs`}
        />
      </div>

      {/* Player table */}
      <Card
        title={`${rows.length} players`}
        subtitle="Rank = scarcity-weighted risk-adjusted value (F9). Cov > 0 means the player adds correlated risk to YOUR roster; ΔWins is the season-win impact of acquiring him."
      >
        {rows.length === 0 ? (
          <EmptyState icon="🔍" message="No players match the current filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-500 text-xs uppercase">
                  <th className="text-left font-medium pb-2 pr-3">#</th>
                  <th className="text-left font-medium pb-2 pr-3">Player</th>
                  <th className="text-left font-medium pb-2 pr-3">Pos</th>
                  <th className="text-right font-medium pb-2 pr-3">Bye</th>
                  <th className="text-right font-medium pb-2 pr-3">Consensus</th>
                  <th className="text-right font-medium pb-2 pr-3">σ</th>
                  <th className="text-right font-medium pb-2 pr-3">VORP</th>
                  <th className="text-right font-medium pb-2 pr-3">Cov</th>
                  <th className="text-right font-medium pb-2 pr-3">Bye Pen.</th>
                  <th className="text-right font-medium pb-2 pr-3">RAV</th>
                  <th className="text-right font-medium pb-2">ΔWins</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((entry) => (
                  <PlayerRow
                    key={entry.player.id}
                    entry={entry}
                    expanded={expanded === entry.player.id}
                    onToggle={() =>
                      setExpanded(expanded === entry.player.id ? null : entry.player.id)
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

function PlayerRow({
  entry,
  expanded,
  onToggle,
}: {
  entry: RankedPlayer
  expanded: boolean
  onToggle: () => void
}) {
  const a = entry.analytics
  const covTone = a.rosterCovariance > 0.5 ? 'text-amber-600 dark:text-amber-400' : a.rosterCovariance < -0.5 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'
  return (
    <>
      <tr
        onClick={onToggle}
        className="border-t border-slate-200/60 dark:border-slate-700/60 cursor-pointer hover:bg-slate-200/20 dark:hover:bg-slate-700/20 transition-colors"
      >
        <td className="py-2 pr-3 text-slate-500">{entry.rank}</td>
        <td className="py-2 pr-3 text-slate-900 dark:text-white whitespace-nowrap">
          {entry.player.name}
          <span className="text-slate-500 ml-2 text-xs">{entry.player.nflTeam}</span>
          {entry.player.rosterStatus === 'mine' && <Chip tone="blue">mine</Chip>}
          {entry.player.rosterStatus === 'taken' && (
            <span className="text-slate-400 dark:text-slate-600 text-xs ml-1">({entry.player.ownedByTeam})</span>
          )}
        </td>
        <td className="py-2 pr-3">
          <Chip tone={POSITION_TONES[entry.player.position]}>{entry.player.position}</Chip>
        </td>
        <td className="py-2 pr-3 text-right text-slate-500 dark:text-slate-400">{entry.player.byeWeek}</td>
        <td className="py-2 pr-3 text-right text-slate-800 dark:text-slate-200">{fmtPts(a.consensusPoints, 0)}</td>
        <td className="py-2 pr-3 text-right text-slate-500 dark:text-slate-400">±{fmtPts(a.projectionStdDev, 0)}</td>
        <td className="py-2 pr-3 text-right text-slate-800 dark:text-slate-200">{fmtPts(a.vorp, 0)}</td>
        <td className={`py-2 pr-3 text-right ${covTone}`}>{fmtSigned(a.rosterCovariance, 1)}</td>
        <td className="py-2 pr-3 text-right text-slate-500 dark:text-slate-400">
          {a.byeOverlapPenalty > 0 ? `−${fmtPts(a.byeOverlapPenalty, 1)}` : '—'}
        </td>
        <td className="py-2 pr-3 text-right text-slate-900 dark:text-white font-medium">{fmtPts(a.scarcityWeightedValue, 1)}</td>
        <td
          className={`py-2 text-right ${
            a.expectedWinsDelta > 0.005 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500'
          }`}
        >
          {entry.player.rosterStatus === 'available' ? fmtSigned(a.expectedWinsDelta, 2) : '—'}
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-slate-200/30 dark:border-slate-700/30 bg-slate-100/40 dark:bg-slate-900/40">
          <td colSpan={11} className="py-3 px-4">
            {a.notes.length === 0 ? (
              <p className="text-slate-500 text-xs">No correlation, bye, schedule, or injury flags for this player.</p>
            ) : (
              <ul className="text-slate-500 dark:text-slate-400 text-xs space-y-1 list-disc list-inside">
                {a.notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            )}
            <p className="text-slate-400 dark:text-slate-600 text-xs mt-2">
              ADP {entry.player.adp} · replacement level {fmtPts(a.replacementPoints, 0)} pts · injury risk{' '}
              {entry.player.injuryRisk}
            </p>
          </td>
        </tr>
      )}
    </>
  )
}
